import filter from 'lodash/filter';
import find from 'lodash/find';
import slice from 'lodash/slice';
import sampleSize from 'lodash/sampleSize';
import type { TranslationKey } from '../i18n/i18n';
import type { ImageKey } from '../assets/Assets';
import type { MobilityValues } from './Mobility';
import type { Points } from './Score';
import type { Concept, ConceptKey, RequirementKey } from './Concepts';
import personas from './personas.json';

export type PersonaKey =
	| 'aische'
	| 'ben'
	| 'helga'
	| 'horst'
	| 'leon'
	| 'maxi'
	| 'mel'
	| 'pete'
	| 'samira'
	| string;

export type ImportanceValues = {
	readonly [key in RequirementKey]: number;
};

export interface Texts {
	readonly name: TranslationKey;
	readonly title: TranslationKey;
	readonly description: TranslationKey;
	readonly shortDescription: TranslationKey;
}

export interface ReactionImages {
	readonly positive: ImageKey;
	readonly negative: ImageKey;
}

export interface Images {
	readonly face: ReactionImages;
	readonly body: ReactionImages;
}

export interface Reaction {
	readonly conceptName: ConceptKey;
	readonly reaction: TranslationKey;
	/**
	 * Happiness points
	 */
	readonly points: Points;
}

export interface Persona {
	readonly name: PersonaKey;
	readonly importance: ImportanceValues;
	readonly mobilities: MobilityValues;
	readonly reactions: Reaction[];
	readonly texts: Texts;
	readonly images: Images;
}

export type Personas = Persona[];

export const getPersonas = (): Personas => personas;

export const NARRATOR: Persona = {
	name: 'Ellen',
	importance: {
		capacityLoad: 0,
		capacityPersons: 0,
		timeToDrive: 0,
		reliability: 0,
		combinability: 0,
		weatherProtection: 0,
		travelDuration: 0,
		activity: 0,
		customizabilityPrestige: 0,
		costEfficency: 0,
		environment: 0,
		qualityOfLife: 0
	},
	mobilities: {
		car: 0,
		bus: 0,
		bike: 0
	},
	reactions: [],
	texts: {
		name: 'narrator.name',
		title: 'narrator.title',
		description: 'narrator.description',
		shortDescription: 'narrator.shortDescription'
	},
	images: {
		face: {
			positive: 'narrator.face',
			negative: 'narrator.face'
		},
		body: {
			positive: 'narrator.body',
			negative: 'narrator.body'
		}
	}
};

export const isSamePersona = (
	persona1: Persona,
	persona2?: Persona
): boolean => {
	return persona1.name === persona2?.name;
};

export const findPersonaByName = (name: string): Persona | undefined => {
	return find(getPersonas(), ['name', name]);
};

export const containsPersona = (
	personas: Personas,
	persona: Persona
): boolean => {
	return !!find(personas, ['name', persona.name]);
};

export const availablePersonas = (excludes: Persona[]): Persona[] => {
	return filter(
		getPersonas(),
		(persona) => !containsPersona(excludes, persona)
	);
};

export const getOrderedPersonas = (
	count: number,
	excludes: Persona[] = []
): Persona[] => {
	const available = availablePersonas(excludes);
	return slice(available, 0, count);
};

export const getRandomPersonas = (
	count: number,
	excludes: Persona[] = []
): Persona[] => {
	const available = availablePersonas(excludes);
	return sampleSize(available, count);
};

export const findReaction = (
	persona: Persona,
	concept: Concept
): Reaction | undefined => {
	return find(
		persona.reactions,
		(reaction) => reaction.conceptName === concept.name
	);
};
